import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { logInfo } from "@/services/eventLogService";

/**
 * Tracks screen views (route changes) and app lifecycle events.
 * Place once in a top-level component (e.g. App.tsx).
 */
export function useScreenTracking() {
  const location = useLocation();
  const prevRoute = useRef<string | null>(null);
  const routeEnteredAt = useRef(Date.now());

  // ── Screen views ──────────────────────────────────────────────
  useEffect(() => {
    const now = Date.now();
    const timeOnPrevious = prevRoute.current
      ? Math.round((now - routeEnteredAt.current) / 1000)
      : 0;

    // Canonical screen-view event (past-tense, PostHog-idiomatic). This
    // route-based hook is the single source — the old 5-page useTrackScreen
    // (which also emitted "screen_viewed") was deleted to stop double-counting.
    logInfo("navigation", "screen_viewed", {
      route: location.pathname,
      previousRoute: prevRoute.current,
      timeOnPreviousScreen: timeOnPrevious,
    });

    prevRoute.current = location.pathname;
    routeEnteredAt.current = now;
  }, [location.pathname]);

  // ── App lifecycle ─────────────────────────────────────────────
  useEffect(() => {
    const appOpenedAt = Date.now();

    logInfo("navigation", "app_opened", {
      isReturningUser: !!localStorage.getItem("sy_last_contact"),
      lastSeenAt: localStorage.getItem("sy_last_seen") || null,
    });

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        const sessionDuration = Math.round((Date.now() - appOpenedAt) / 1000);
        logInfo("navigation", "app_backgrounded", {
          currentRoute: window.location.pathname,
          sessionDurationSeconds: sessionDuration,
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // ── App load time ─────────────────────────────────────────────
  useEffect(() => {
    const measure = () => {
      try {
        const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
        if (nav) {
          logInfo("performance", "app_load_time", {
            durationMs: Math.round(nav.loadEventEnd - nav.startTime),
            isFirstVisit: !localStorage.getItem("sy_last_contact"),
          });
        }
      } catch {
        // Silent
      }
    };

    if (document.readyState === "complete") {
      setTimeout(measure, 100);
    } else {
      window.addEventListener("load", () => setTimeout(measure, 100));
    }
  }, []);
}
